import {proxy} from "valtio";

import type {BookingInfo} from "@/entities/booking";
import {RentalTerms} from "@/entities/booking/api/types";
import type {VacasaUnitResponse} from "@/entities/rental";

import {getMonthTranslationKey, getWeekdayTranslationKey} from "@/shared/i18n";
import {DateHelper} from "@/shared/lib";
import {IValtioModel} from "@/shared/models/base-model.interface";

export interface CheckInOutDetails {
    weekday: string;
    date: string;
    time: string;
}

interface BookingDetailsConfig {
    booking: BookingInfo;
    unit: VacasaUnitResponse;
    rentalTerms?: RentalTerms;
}

class BookingDetailsClass implements IValtioModel {
    private readonly booking: BookingInfo;
    private readonly unit: VacasaUnitResponse;
    readonly rentalTerms: RentalTerms | undefined;

    constructor(config: BookingDetailsConfig) {
        this.booking = config.booking;
        this.unit = config.unit;
        this.rentalTerms = config.rentalTerms;
    }

    // ==================== Unit ====================

    get unitId(): string {
        return this.booking.unitId;
    }

    // ==================== Dates ====================

    get checkIn(): string {
        return this.booking.checkIn;
    }

    get checkOut(): string {
        return this.booking.checkOut;
    }

    private formatDateDetails(dateString: string, timeString: string | null | undefined): CheckInOutDetails {
        const date = DateHelper.parseLocalDate(dateString)!;
        const weekdayName = DateHelper.formatWeekday(date);
        const dateLong = DateHelper.formatDateLong(date);

        // Convert weekday to translation key
        const weekdayKey = getWeekdayTranslationKey(weekdayName);

        // Parse date to extract month key
        const parts = dateLong.split(" ");
        let dateKey = dateLong;
        if (parts.length === 3) {
            const month = parts[0]; // "Dec" or "May"
            const day = parts[1].replace(",", "");
            const year = parts[2];

            const monthKey = getMonthTranslationKey(month);
            if (monthKey && monthKey !== month) {
                // Return structure that can be used for translation
                dateKey = `${day} ${monthKey} ${year}`;
            }
        }

        return {
            weekday: weekdayKey,
            date: dateKey,
            time: DateHelper.formatTime24to12(timeString),
        };
    }

    get checkInFormatted(): CheckInOutDetails {
        return this.formatDateDetails(this.booking.checkIn, this.unit.checkInTime);
    }

    get checkOutFormatted(): CheckInOutDetails {
        return this.formatDateDetails(this.booking.checkOut, this.unit.checkOutTime);
    }

    get nights(): number {
        return DateHelper.calculateNights(this.booking.checkIn, this.booking.checkOut);
    }

    // ==================== Guests ====================

    get adults(): number {
        return this.booking.adults;
    }

    get children(): number {
        return this.booking.children;
    }

    get pets(): number {
        return this.booking.pets;
    }

    get totalGuests(): number {
        return this.booking.adults + this.booking.children;
    }

    get guestsText(): string {
        const parts: Array<string> = [];

        // Adults
        const adults = this.booking.adults;
        parts.push(`${adults} Adult${adults !== 1 ? "s" : ""}`);

        // Children
        const children = this.booking.children;
        parts.push(`${children} ${children !== 1 ? "Children" : "Child"}`);

        // Dogs
        const pets = this.booking.pets;
        parts.push(`${pets} Dog${pets !== 1 ? "s" : ""}`);

        return parts.join(", ");
    }

    /** Promo/coupon code applied to this booking (from checkout session) */
    get couponCode(): string | undefined {
        return this.booking.couponCode;
    }

    init(): void | Promise<void> {
        // No initialization needed
    }
}

export type BookingDetailsInstance = BookingDetailsClass;

export function BookingDetails(config: BookingDetailsConfig): BookingDetailsInstance {
    return proxy(new BookingDetailsClass(config));
}
